import { PACKAGE_VERSION_MAPPING, VersionMapping } from '@/use/use-application-version'
import { every, find, includes, isEmpty } from 'lodash'
import { Route } from 'vue-router'
import { routesConfig } from '@/utils/routes/config'
import { isAllowedRailsUrl } from '@/utils/routes/helper'

const NUMBER_REGEX = /^\d+$/

type appVersion = 'classic' | 'legacy' | 'advanced'
type companyType = 'broker' | 'vendor' | 'client'

export interface BestMatchRouteReturn {
  route: string | null
  isAccessible: boolean
  reason?: string
  isExceptionSuccess?: boolean
}

function matchesResolveQuery (resolve: string, q: any): boolean {
  if (Object.keys(q ?? {}).length) {
    return !(resolve.includes('?')) ||
      [...new URLSearchParams(resolve.split('?')[1] ?? '')].every(
        ([k, v]) => (Array.isArray(q[k]) ? q[k] : q[k] == null ? [] : [q[k]]).includes(v)
      )
  } else {
    return true
  }
}

export function findBestMatchingRoute (
  currentRoute: Route,
  fromVersion: appVersion,
  targetVersion: appVersion,
  companyType: companyType,
  isRetry = false,
  isRevert = false
): BestMatchRouteReturn {
  const endFallbackpath = '/users/settings'
  const fromVersionLabel = PACKAGE_VERSION_MAPPING.find((info: VersionMapping) => info.packageValue === fromVersion)?.label || ''
  const targetVersionLabel = PACKAGE_VERSION_MAPPING.find((info: VersionMapping) => info.packageValue === targetVersion)?.label || ''

  const path = currentRoute.path.replace(/^\/|\/$/g, '')
  const companyTypeRoutes = routesConfig[companyType]
  const hasAccess = true

  if (!companyTypeRoutes) {
    return {
      route: endFallbackpath,
      isAccessible: hasAccess,
      reason: 'Version switching is currently unavailable. Please contact Support for assistance.'
    }
  }

  if (isAllowedRailsUrl(currentRoute.path)) {
    return {
      route: currentRoute.fullPath,
      isAccessible: hasAccess,
      isExceptionSuccess: targetVersion !== 'advanced'
    }
  }

  const type = includes(path, '/edit')
    ? 'edit'
    : includes(path, '/details') || includes(path, '/profile') || /\/\d+/.test(path)
      ? 'details'
      : 'list'

  const idMatch = path.match(/\/(\d+)/)
  const id = idMatch?.[1]

  for (const [_moduleKey, routes] of Object.entries(companyTypeRoutes)) {
    const versionRoutes = routes[fromVersion]
    const targetVersionRoutes = routes[targetVersion]
    if (!versionRoutes) { continue }

    const priorities = type === 'edit'
      ? ['edit', 'details', 'list']
      : type === 'details'
        ? ['details', 'list', 'edit']
        : ['list', 'details', 'edit']

    for (const priority of priorities) {
      const routeObj = find(versionRoutes, priority)
      if (!routeObj) { continue }

      const config = routeObj[priority]
      const pattern = config.resolve.split('?')[0].replace(/^\/|\/$/g, '')
      const pathSegments = path.split('/')
      const patternSegments = pattern.split('/')

      let isPathMatched = false
      if (isRevert) {
        isPathMatched = every(patternSegments, (seg: string, i: number) =>
          seg.startsWith(':') ? NUMBER_REGEX.test(pathSegments[i]) : seg === pathSegments[i])
      } else {
        isPathMatched = pathSegments.length === patternSegments.length && every(pathSegments, (seg: string, i: number) => {
          const patternSeg = patternSegments[i]
          return seg === patternSeg || (patternSeg.startsWith(':') && NUMBER_REGEX.test(seg))
        })
      }

      const isMatch = isPathMatched && matchesResolveQuery(config.resolve, currentRoute.query)

      if (isMatch) {
        const targetRouteObj = find(targetVersionRoutes, priority)

        if (!targetRouteObj) {
          return {
            route: endFallbackpath,
            isAccessible: hasAccess,
            reason: `Requested path exists in '${fromVersionLabel}' but not in '${targetVersionLabel}'.`
          }
        }
        // reason: `Requested path '${priority}' exists in ${fromVersion} but not in ${targetVersion}`,

        const targetConfig = targetRouteObj[priority]
        let result = targetConfig.resolve

        // Replacing ID parameter to :id or :workOrderId / :purchaseOrderId / ...
        if (id) {
          const idParam = result.match(/:(\w+Id|id)/)?.[0]
          if (idParam) {
            result = result.replace(idParam, id)
          }
        }

        if (targetConfig.query && !isEmpty(currentRoute.query)) {
          const separator = includes(result, '?') ? '&' : '?'
          const newQuery = new URLSearchParams(currentRoute.query as Record<string, string>)
          if (targetVersion === 'advanced') {
            for (const [key, _value] of newQuery) {
              if (['tab', 'selectedTab'].includes(key)) {
                newQuery.delete(key)
              }
            }
          }
          if (newQuery.size) {
            result += separator + newQuery.toString()
          }
        }

        return {
          route: result.startsWith('/') ? result : `/${result}`,
          isAccessible: hasAccess
        }
      }
    }
  }

  // If no match and not a retry, appending /inbound -> as /company/proposals = /company/proposal/inbound
  if (!isRetry && !includes(path, '/inbound') && !includes(path, '/outbound')) {
    const modifiedPath = currentRoute.path.replace(/([^/]+)$/, '$1/inbound')
    if (modifiedPath !== currentRoute.path) {
      return findBestMatchingRoute(
        { ...currentRoute, path: modifiedPath } as Route,
        fromVersion,
        targetVersion,
        companyType,
        true
      )
    }
  }

  if (!isRevert) {
    return findBestMatchingRoute(
      currentRoute,
      fromVersion,
      targetVersion,
      companyType,
      isRetry,
      true
    )
  }

  // return {
  //   route: null,
  //   isAccessible: false,
  // }
  return {
    route: endFallbackpath,
    isAccessible: hasAccess,
    reason: `Requested path doesn't exists in '${targetVersionLabel}'.`
  }
}
